import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import type { TicketDetail, TicketDetailArtifact } from '@nightshift/ui-types';

import { Markdown } from '../../components/markdown.tsx';
import { Spinner } from '../../components/primitives.tsx';
import { api } from '../../live/api.ts';
import { useTimeFormat } from '../../lib/display.ts';
import { clockStamp, observedTime } from '../../lib/format.ts';
import './review-plan.css';

/** The newest non-superseded plan is the contract currently awaiting a decision. */
export function currentPlanArtifact(detail: Pick<TicketDetail, 'artifacts'>): TicketDetailArtifact | null {
  return [...detail.artifacts].reverse().find((row) => row.kind === 'plan' && !row.superseded) ?? null;
}

/**
 * What the plan load watches: the artifact's identity and its inline body, never its object.
 *
 * The drawer reloads the ticket on every board tick, which builds a fresh artifact object each
 * time. Keying the load on that object re-ran it every few seconds -- the text was blanked, the
 * panel collapsed to its header and footer, "Loading the complete plan" flashed up, and Approve,
 * disabled while the text is null, went dead for as long as the refetch took. An artifact is
 * immutable, so two reads of the same plan return equal values here, and a genuinely new plan
 * still has a new id.
 */
export function planSource(
  detail: Pick<TicketDetail, 'artifacts'>,
): { planId: number | null; inlineText: string | null } {
  const plan = currentPlanArtifact(detail);
  return {
    planId: plan?.id ?? null,
    inlineText: plan && !plan.refTruncated ? plan.ref : null,
  };
}

/**
 * One deliberate checkpoint: read the complete plan and ticket conversation, then decide.
 * The drawer intentionally exposes no plan decision before this panel opens.
 */
export function ReviewPlanPanel({
  detail,
  now,
  busy,
  onCancel,
  onDecision,
}: {
  detail: TicketDetail;
  now: number;
  busy: boolean;
  onCancel: () => void;
  onDecision: (decision: 'approve' | 'defer') => void;
}) {
  const panelRef = useRef<HTMLElement>(null);
  const plan = currentPlanArtifact(detail);
  const { planId, inlineText } = planSource(detail);
  const [text, setText] = useState<string | null>(inlineText);
  const [error, setError] = useState<string | null>(null);
  const format = useTimeFormat();

  useEffect(() => {
    let active = true;
    setError(null);
    if (planId === null || inlineText !== null) {
      setText(inlineText);
      return () => {
        active = false;
      };
    }
    setText(null);
    void api
      .artifactText(planId)
      .then((body) => {
        if (active) setText(body.text);
      })
      .catch((caught) => {
        if (active) setError(caught instanceof Error ? caught.message : String(caught));
      });
    return () => {
      active = false;
    };
  }, [planId, inlineText]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>('button:not(:disabled), [href]'));
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const loading = Boolean(plan) && text === null && error === null;

  return createPortal(
    <>
      <div className="review-plan-scrim" role="presentation" onClick={onCancel} />
      <section
        ref={panelRef}
        className="review-plan rise"
        role="dialog"
        aria-modal="true"
        aria-labelledby="review-plan-title"
      >
        <header className="review-plan-head">
          <div>
            <p className="drawer-section-kicker review-plan-kicker">Plan approval</p>
            <h2 id="review-plan-title" className="review-plan-title">
              {detail.ticket.ticketKey} <span className="dim">{detail.ticket.title}</span>
            </h2>
          </div>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onCancel} autoFocus>
            Close
          </button>
        </header>

        <div className="review-plan-body scroll-y">
          <section className="review-plan-document" aria-labelledby="review-plan-document-title">
            <h3 id="review-plan-document-title" className="drawer-section-kicker">
              Proposed plan
            </h3>
            {!plan ? (
              <p className="review-plan-empty">No plan artifact is attached to this approval request.</p>
            ) : error ? (
              <p className="review-plan-error" role="alert">
                Could not load the complete plan — {error}
              </p>
            ) : loading ? (
              <p className="review-plan-loading">
                <Spinner size={14} /> Loading the complete plan…
              </p>
            ) : (
              <div className="review-plan-markdown">
                <Markdown source={text ?? ''} />
              </div>
            )}
          </section>

          <section className="review-plan-comments" aria-labelledby="review-plan-comments-title">
            <div className="review-plan-section-head">
              <h3 id="review-plan-comments-title" className="drawer-section-kicker">
                Comments
              </h3>
              <span className="dim num">{detail.comments.length}</span>
            </div>
            {detail.comments.length === 0 ? (
              <p className="review-plan-empty dim">No comments on this ticket.</p>
            ) : (
              <ol className="review-plan-comment-list">
                {detail.comments.map((comment) => (
                  <li key={comment.id} className="review-plan-comment">
                    <div className="review-plan-comment-meta">
                      <strong>{comment.author}</strong>
                      <time
                        className="dim num"
                        dateTime={new Date(comment.createdAt).toISOString()}
                        title={clockStamp(comment.createdAt)}
                      >
                        {observedTime(comment.createdAt, now, format)}
                      </time>
                    </div>
                    <Markdown source={comment.body} />
                  </li>
                ))}
              </ol>
            )}
          </section>
        </div>

        <footer className="review-plan-foot">
          <p className="review-plan-fineprint dim">
            Approve starts implementation. Defer keeps the ticket waiting without changing the plan.
          </p>
          <div className="review-plan-actions">
            <button type="button" className="btn" disabled={busy} onClick={() => onDecision('defer')}>
              Defer plan
            </button>
            <button
              type="button"
              className="btn btn-accent"
              disabled={busy || loading || text === null}
              onClick={() => onDecision('approve')}
            >
              {busy ? <Spinner /> : null} Approve plan
            </button>
          </div>
        </footer>
      </section>
    </>,
    document.body,
  );
}
